/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {AssetState, EntityStatus} from '@ion/core/src/types/entities';
import {AssetStatusIcon} from '../../../src/icons/AssetStatusIcon';
import {NoIgnitionIcon} from '../../../src/icons/NoIgnitionIcon';
import {dlsAlt, dlsColor, dlsTypography} from '../../../src/theme/theme';
import {BodyTypography} from '../../../src/typography/BodyTypography';
import ConditionalRender from '../../render/ConditionalRender';

interface AssetStatusItemType {
  assetStatus: AssetState | EntityStatus | string;
  isCustomVariant?: boolean;
  value: string;
  dateTime?: string;
  fromCard?: boolean;
  fontSize?: string;
  speed?: string;
  color?: string;
  disabled?: boolean;
  statusWithIcon?: any;
  scDevice?: boolean;
}

const useStyles = makeStyles(theme => ({
  statusItem : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : ({fromCard}: AssetStatusItemType) => fromCard ? 'flex-end' : 'initial'
  },
  dateTime : {
    ...dlsTypography('slider'),
    color          : dlsColor('grey3'),
    display        : 'flex',
    justifyContent : 'flex-end',
    marginBottom   : '6px',
    fontSize       : ({statusWithIcon}: AssetStatusItemType) => statusWithIcon ? '8px' : dlsTypography('slider').fontSize
  },
  altDateTime : {
    fontWeight    : 500,
    fontSize      : '10px',
    lineHeight    : '16px',
    letterSpacing : '1px'
  },
  statusItemTypography : {
    textAlign : 'right',
    color     : (props: AssetStatusItemType) => props.color || (props.disabled ? dlsColor('grey4') : dlsColor('grey3')),
    fontSize  : (props: AssetStatusItemType) => props.fontSize
  },
  altStatusItemTypography : {
    textAlign     : 'right',
    fontWeight    : 700,
    fontSize      : '12px',
    lineHeight    : '16px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : `${dlsColor('grey2')} !important`,
    padding       : '0 !important'
  },
  statusIconWrapper : {
    alignItems : 'center',
    display    : 'flex',
    gap        : ({statusWithIcon}: AssetStatusItemType) => statusWithIcon ? '6px' : 'unset'
  }
}));

const AssetStatusItem = (props: AssetStatusItemType) => {
  const {
    assetStatus,
    isCustomVariant,
    value,
    speed,
    dateTime,
    statusWithIcon = undefined,
    scDevice = false
  } = props;
  const classes = useStyles(props);
  const isTowedState = value === AssetState.TOWED;
  return (
    <div className={classes.statusItem}>
      <BodyTypography className={`
        ${isCustomVariant ? classes.altStatusItemTypography : classes.statusItemTypography}
        ${statusWithIcon ? classes.statusIconWrapper : null}
      `}>
        <>
          <ConditionalRender condition={!statusWithIcon}>
            {isTowedState && <NoIgnitionIcon size={15} />}
            <AssetStatusIcon status={assetStatus} />
          </ConditionalRender>
          {statusWithIcon}
          <ConditionalRender condition={scDevice && !isEmpty(value)}>
            {value}
          </ConditionalRender>
          <ConditionalRender condition={value && !scDevice}>
            {`${translate(`common:assetState.${value}`)} ${speed ? `(${speed})` : ''}`}
          </ConditionalRender>
          {!statusWithIcon && (<br />)}
          <ConditionalRender condition={dateTime}>
            <label className={isCustomVariant ? classes.altDateTime : classes.dateTime}>
              {dateTime}
            </label>
          </ConditionalRender>
        </>
      </BodyTypography>
    </div>
  );
};

export default AssetStatusItem;
